import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { sdk } from '@/graphql/client'
import { useAuthStore } from './auth'
import { useSettingsStore } from './settings'
import type {
    ParsedCommand,
    ParsedLogin,
    ParsedTicketsetsT,
    ParsedTicketsetsZ
} from '@/services/configCode'

export interface Ticketset {
    id: string
    name: string
    customName?: string | null
    validate?: boolean | null
    count?: number | null
    ticketCount?: number | null
    selected: boolean
}

export interface EventItem {
    id: string
    name: string
    startAt: string | null
    endAt: string | null
    ticketsets: Ticketset[]
}

export interface Zone {
    id: string
    name: string
    selected?: boolean
}

export interface ApplyConfigResult {
    ok: boolean
    applied: Array<
        | { type: 'LOGIN'; username: string }
        | { type: 'TICKETS_T'; ids: number[] }
        | { type: 'TICKETS_Z'; ids: number[] }
        | { type: 'COUNTER'; mode: 0 | 1 }
        | { type: 'LANGUAGE'; lang: string }
        | { type: 'SCAN_INFO'; mode: string }
    >
    errors: string[]
}

export interface ZoneStat {
    name: string
    today: number | string
    total: number | string
}

export const useEventsStore = defineStore('events', () => {
    const events = ref<EventItem[]>([])
    const zones = ref<Zone[]>([])
    const activeEvents = ref<string[]>([])
    // IDs of ticketsets that the user explicitly selected for the current scan session.
    // Stays in store across re-loads of `events` (ts.selected flag gets reset on each load).
    const activeTicketsetIds = ref<string[]>([])
    // Checkpoints byly odstraněny ze serveru. Zachováváme stub pro persistovaný state.
    const activeCheckpoint = ref<string | null>(null)
    const activeZones = ref<number[]>([])
    const lastZones = ref<string | null>(null)
    const lastZoneLoadDate = ref<string | null>(null)
    const zoneStats = ref<ZoneStat[]>([])

    async function loadEvents(days = 0): Promise<EventItem[]> {
        const dayMs = 86_400_000
        const fromDate = new Date(Date.now() - days * dayMs).toISOString().substring(0, 19)
        const result = await sdk.MobileEvents({ fromDate })
        const raw = result.mobile?.events ?? []
        const activeSet = new Set(activeTicketsetIds.value.map(String))
        const list: EventItem[] = raw
            .filter((e): e is NonNullable<typeof e> => e !== null && e.id !== null && e.id !== undefined)
            .map(e => ({
                id: String(e.id),
                name: e.name ?? '',
                startAt: e.startAt ?? null,
                endAt: e.endAt ?? null,
                ticketsets: (e.ticketsets ?? [])
                    .filter((ts): ts is NonNullable<typeof ts> => ts !== null && ts.id !== null && ts.id !== undefined)
                    .map(ts => ({
                        id: String(ts.id),
                        name: ts.name ?? '',
                        customName: ts.customName ?? null,
                        validate: ts.validate ?? null,
                        count: ts.count ?? null,
                        selected: activeSet.has(String(ts.id))
                    }))
            }))
        events.value = list
        return list
    }

    async function loadZones(): Promise<Zone[]> {
        const result = await sdk.Zones()
        const raw = result.mobile?.zones ?? []
        const list: Zone[] = raw
            .filter((z): z is NonNullable<typeof z> => z !== null && z.id !== null && z.id !== undefined)
            .map(z => ({ id: String(z.id), name: z.name ?? '', selected: false }))
        zones.value = list
        return list
    }

    async function loadZoneStats(day: string) {
        const result = await sdk.MobileZoneStats({ day })
        const raw = result.mobile?.zoneStats ?? []
        zoneStats.value = raw
            .filter((z): z is NonNullable<typeof z> => z !== null)
            .map(z => ({
                name: z.name ?? '',
                today: (z.today ?? 0) as number | string,
                total: (z.total ?? 0) as number | string
            }))
    }

    function setActiveEvents(ids: string[]) {
        activeEvents.value = ids
    }

    function setActiveTicketsetIds(ids: string[]) {
        activeTicketsetIds.value = ids
    }

    function setActiveCheckpoint(id: string | null) {
        activeCheckpoint.value = id
    }

    function setActiveZones(ids: number[]) {
        activeZones.value = ids
    }

    function setLastZones(value: string | null) {
        lastZones.value = value
    }

    function setLastZoneLoadDate(value: string | null) {
        lastZoneLoadDate.value = value
    }

    async function applyConfigCode(commands: ParsedCommand[]): Promise<ApplyConfigResult> {
        const result: ApplyConfigResult = { ok: true, applied: [], errors: [] }

        const hasZones = commands.some(c => c.type === 'TICKETS_Z')
        const hasSets = commands.some(c => c.type === 'TICKETS_T')
        if (hasZones && hasSets) {
            result.ok = false
            result.errors.push('Kód nesmí kombinovat sady a zóny')
            return result
        }

        // 1) LOGIN první
        const loginCmd = commands.find((c): c is ParsedLogin => c.type === 'LOGIN')
        if (loginCmd) {
            const auth = useAuthStore()
            try {
                const res = await auth.loginUser({ username: loginCmd.username, password: loginCmd.password })
                if (res.ok) {
                    result.applied.push({ type: 'LOGIN', username: loginCmd.username })
                } else {
                    result.ok = false
                    result.errors.push(res.message || 'Přihlášení selhalo')
                    return result
                }
            } catch {
                result.ok = false
                result.errors.push('Přihlášení selhalo')
                return result
            }
        }

        // 2) Zóny
        const zonesCmd = commands.find((c): c is ParsedTicketsetsZ => c.type === 'TICKETS_Z')
        if (zonesCmd) {
            const zoneIds = zonesCmd.ids
            setActiveEvents([])
            setActiveCheckpoint(null)
            setActiveTicketsetIds([])
            setLastZones(null)
            setActiveZones(zoneIds)
            result.applied.push({ type: 'TICKETS_Z', ids: zoneIds })
        }

        // 3) Sady — načti eventy a dohledej parent eventy, aby Scanner měl populated state
        const setsCmd = commands.find((c): c is ParsedTicketsetsT => c.type === 'TICKETS_T')
        if (setsCmd) {
            // Deep-link / config QR can reference a ticketset whose event is older
            // than the user's olderEvents window — the QR is the explicit intent, so
            // load ALL events (days=9999, same as the Settings "all events" option)
            // to guarantee the set is found. Otherwise an out-of-window set yields no
            // parent event, activeEvents stays empty and the scanner renders nothing.
            let freshEvents: EventItem[]
            try {
                freshEvents = await loadEvents(9999)
            } catch {
                result.ok = false
                result.errors.push('Nepodařilo se načíst akce')
                return result
            }

            const wantedIds = setsCmd.ids.map(String)
            const wantedSet = new Set(wantedIds)
            const eventIds = freshEvents
                .filter(e => e.ticketsets.some(ts => wantedSet.has(ts.id)))
                .map(e => e.id)
            const foundIds = new Set<string>()
            for (const e of freshEvents) {
                for (const ts of e.ticketsets) {
                    if (wantedSet.has(ts.id)) foundIds.add(ts.id)
                }
            }
            const missing = wantedIds.filter(id => !foundIds.has(id))

            setActiveZones([])
            setActiveCheckpoint(null)
            setActiveEvents(eventIds)
            setActiveTicketsetIds(wantedIds)

            result.applied.push({ type: 'TICKETS_T', ids: setsCmd.ids })
            if (missing.length) {
                result.errors.push(`Sady nenalezeny: ${missing.join(', ')}`)
            }
        }

        // 4) COUNTER → toggle showTicketCount in settings
        const counterCmd = commands.find((c): c is Extract<ParsedCommand, { type: 'COUNTER' }> => c.type === 'COUNTER')
        if (counterCmd) {
            const settings = useSettingsStore()
            settings.showTicketCount = counterCmd.mode === 1
            result.applied.push({ type: 'COUNTER', mode: counterCmd.mode })
        }

        // 5) LANGUAGE → settings.lang
        const langCmd = commands.find((c): c is Extract<ParsedCommand, { type: 'LANGUAGE' }> => c.type === 'LANGUAGE')
        if (langCmd) {
            const settings = useSettingsStore()
            settings.lang = langCmd.lang as typeof settings.lang
            result.applied.push({ type: 'LANGUAGE', lang: langCmd.lang })
        }

        // 6) SCAN_INFO → settings.group mapping
        const scanCmd = commands.find((c): c is Extract<ParsedCommand, { type: 'SCAN_INFO' }> => c.type === 'SCAN_INFO')
        if (scanCmd) {
            const settings = useSettingsStore()
            switch (scanCmd.mode) {
                case 'NONE':            settings.group = ''; break
                case 'PRICE':           settings.group = 'price'; break
                case 'TICKETSET':       settings.group = 'ticketset'; break
                case 'TICKETSET-PRICE': settings.group = 'ticketset_price'; break
            }
            result.applied.push({ type: 'SCAN_INFO', mode: scanCmd.mode })
        }

        return result
    }

    const eventById = computed(() => (id: string) => events.value.find(e => e.id === id))

    const activeCheckpointObject = computed(() => null)

    return {
        events,
        zones,
        activeEvents,
        activeTicketsetIds,
        activeCheckpoint,
        activeZones,
        lastZones,
        lastZoneLoadDate,
        zoneStats,
        loadEvents,
        loadZones,
        loadZoneStats,
        setActiveEvents,
        setActiveTicketsetIds,
        setActiveCheckpoint,
        setActiveZones,
        setLastZones,
        setLastZoneLoadDate,
        applyConfigCode,
        eventById,
        activeCheckpointObject
    }
})
